/**
 * Usage strip: the three bars, and the waiting state that comes before them.
 *
 * It exists because two screens show the same three numbers (the overview's
 * zone 5 and the projects list's account line) and because zone 5 is the one
 * part of the overview that is allowed to arrive late. Screen spec S7 makes
 * that a build requirement rather than a nicety: zones 1 to 4 come from data
 * the page already has, this strip resolves afterwards, and the page has to be
 * meaningful and actionable while this is still three grey tracks.
 *
 * So the waiting state is part of the component, not part of the screen. A
 * screen that had to remember to render skeletons is a screen that will
 * eventually render a spinner instead.
 *
 * Zero is a real state with its own words, handled inside the usage bar: bars
 * at zero read "No traffic yet", never an empty track and never grey as
 * broken. A brand new project has not failed at anything.
 */

import { Skeleton } from './Skeleton';
import { UsageBar, type UsageSegment } from './UsageBar';
import { t } from '../../lib/i18n/t';
import type { Usage, UsageBreakdown } from '../../lib/types';
import '../../styles/components/usage-strip.css';

/** Days until the period rolls over, rounded up: 0.4 days left is still today. */
export function daysUntil(iso: string, now = Date.now()): number {
  const ms = new Date(iso).getTime() - now;
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

/**
 * An account meter's parts: the top projects by name, then the rest as
 * "Others". Project names are the owner's own words and stay untranslated.
 */
export function breakdownSegments(breakdown: UsageBreakdown): UsageSegment[] {
  const segments: UsageSegment[] = breakdown.projects.map((p) => ({
    key: p.projectId,
    label: p.name ?? t('Deleted project'),
    used: p.used,
  }));
  if (breakdown.others) {
    segments.push({ key: 'others', label: t('Others'), used: breakdown.others.used });
  }
  return segments;
}

export interface UsageStripProps {
  /** Undefined while the numbers are still resolving. */
  usage: Usage | undefined;
  /** The 3px variant, for the account line under a list of projects. */
  compact?: boolean;
  /** Renders "Resets in 11 days" once, under the last bar. */
  showResets?: boolean;
  /** What is being waited for, in words, for the waiting state. */
  waitingLabel?: string;
}

export function UsageStrip({
  usage,
  compact = false,
  showResets = true,
  waitingLabel,
}: UsageStripProps) {
  if (!usage) {
    return (
      <div className="ustrip" data-compact={compact ? 'yes' : 'no'}>
        <Skeleton shape="track" count={3} label={waitingLabel ?? t('Working out your usage')} />
      </div>
    );
  }

  const resetsInDays = daysUntil(usage.resetsAt);

  return (
    <div className="ustrip" data-compact={compact ? 'yes' : 'no'}>
      {usage.metrics.map((metric, index) => (
        <UsageBar
          key={metric.resource}
          label={t(metric.label)}
          used={metric.used}
          limit={metric.limit}
          unit={metric.unit}
          compact={compact}
          // Compute is the one meter here whose unit means nothing on its
          // own, so it is the one that drops its amount and reads as a
          // percentage instead.
          showPercent={metric.unit === 'count'}
          percentOnly={metric.unit === 'count'}
          // The period is stated once for the whole strip, on the last bar.
          // Three copies of "Resets in 11 days" is three chances to read it
          // as three different periods.
          resetsInDays={showResets && index === usage.metrics.length - 1 ? resetsInDays : undefined}
          // Only the account roll-up carries a breakdown, so a project's own
          // strip draws plain bars.
          segments={metric.breakdown ? breakdownSegments(metric.breakdown) : undefined}
        />
      ))}
    </div>
  );
}
